import { useState, useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useGroupMembership } from '@/hooks/useGroupMembership';
import { fetchSeries, isCommissioner } from '@/lib/supabase-queries';
import { supabase } from '@/integrations/supabase/client';
import { DrillInLayout } from '@/components/DrillInLayout';
import CommissionerTab from '@/components/CommissionerTab';
import { Loader2 } from 'lucide-react';

const ManageDrillIn = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const { user } = useAuth();
  const { isMember, loading: membershipLoading } = useGroupMembership(groupId, user?.id);
  const [group, setGroup] = useState<any>(null);
  const [seriesList, setSeriesList] = useState<any[]>([]);
  const [isCommissionerUser, setIsCommissionerUser] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!groupId || !user || !isMember) return;
    const load = async () => {
      const [{ data: g }, s, commish] = await Promise.all([
        supabase.from('groups').select('*').eq('id', groupId).single(),
        fetchSeries(),
        isCommissioner(user.id, groupId),
      ]);
      setGroup(g);
      setSeriesList(s);
      setIsCommissionerUser(commish);
      setLoading(false);
    };
    load();
  }, [groupId, user, isMember]);

  if (!membershipLoading && isMember === false) return <Navigate to="/" replace />;

  if (loading || membershipLoading) {
    return (
      <DrillInLayout title="ניהול קבוצה" groupId={groupId!}>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-accent" />
        </div>
      </DrillInLayout>
    );
  }

  // Redirect non-commissioners back to main feed
  if (!isCommissionerUser) {
    return <Navigate to={`/group/${groupId}`} replace />;
  }

  const refreshGroup = async () => {
    if (!groupId) return;
    const { data: g } = await supabase.from('groups').select('*').eq('id', groupId).single();
    setGroup(g);
  };

  return (
    <DrillInLayout title="ניהול קבוצה" groupId={groupId!}>
      <CommissionerTab
        groupId={groupId!}
        seriesList={seriesList}
        groupName={group?.name || ''}
        paymentLink={group?.payment_link || null}
        chatLink={group?.chat_link || null}
        onGroupUpdated={refreshGroup}
      />
    </DrillInLayout>
  );
};

export default ManageDrillIn;
